import Utils from '@/utils/QSUtils';
import FormsService from '@/services/2.6/FormsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import IFieldData from '@/models/forms/IFieldData';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { IAdditionalFields, IDataPointsAddFields, newAdditionalFields } from '@/models/AdditionalFields';
import IFormCodesFields from '@/models/forms/IFormCodesFields';
import FormsHelper from './helpers/FormsHelper';
import IFormCodeFields from '@/models/forms/IFormCodeFields';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import SettingsService from '@/services/2.6/SettingsService';
import FormsManager from '@/manager/2.6/FormsManager';

// Internal only: The set of mutation method names as strings.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    updateFormFields: 'updateFormFields',
    updateAdditionalFields: 'updateAdditionalFields ',
};

interface IAddFieldsSave {
    data: IAdditionalFields[];
    formId: string | null;
}

interface IFormFields extends Array<IFormCodeFields> {}

/**
 * This store file is implemented using vuex-module-decorators.
 * https://github.com/championswimmer/vuex-module-decorators
 * https://championswimmer.in/vuex-module-decorators/pages/overview.html
 *
 * Getters at the top, Mutators at the bottom.
 * All Actions have a summary indicating they're an action (intellisense purposes).
 * All Actions are async since a Promise is implemented internally.
 */
@Module({ namespaced: true, name: 'formField' })
export default class FormFieldStoreModule extends VuexModule {
    public formCode = '';

    // Private are the store properties
    private additionalFields: IDataPointsAddFields | null = null;
    private formFields: IFormFields = [];

    get AdditionalFields(): IDataPointsAddFields | null {
        return this.additionalFields;
    }

    get FormFields(): any {
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
        return (this.formFields as any)[formCode];
    }

    /**
     * Action:
     */
    @Action
    async saveDataPreferenceSourceOrder(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveDataPreferenceSourceOrder(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveDataPreferenceSourceOrder bad response');
        }
    }

    @Action
    async savePreferredCasing(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).savePreferredCasing(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.savePreferredCasing bad response');
        }
    }

    @Action
    async saveFieldRules(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveFieldRules(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveFieldRules bad response');
        }
    }

    @Action
    async saveSuggestFormattingRule(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveSuggestFormattingRule(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveSuggestFormattingRule bad response');
        }
    }

    @Action
    async saveAdditionalFields(payload: IAddFieldsSave) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).putAdditionalFields(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreAdditionalFields();
        } else {
            console.log('FormFieldStoreModule.saveAdditionalFields bad response');
        }
    }

    /**
     * Mutation:
     */
    @Mutation
    updateAdditionalFields(payload: IDataPointsAddFields) {
        this.additionalFields = payload;
    }

    @Mutation
    updateFields(payload: IFormCodeFields) {
        if (payload.formCode) {
            (this.formFields as any)[payload.formCode] = payload.formFields;
        }
    }
}
